import * as React from 'react';
import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import type { AnalyticsMeetingType, AnalyticsStatus, MeetingAnalytics } from '../types';

const STATUS_BADGE: Record<
  AnalyticsStatus,
  { label: string; variant: 'success' | 'soft-blue' | 'soft' }
> = {
  live: { label: 'Live', variant: 'success' },
  scheduled: { label: 'Scheduled', variant: 'soft-blue' },
  ended: { label: 'Ended', variant: 'soft' },
};

const TYPE_LABEL: Record<AnalyticsMeetingType, string> = {
  instant: 'Instant',
  scheduled: 'Scheduled',
};

/** Title block for the analytics view — back link, the two names, status chips. */
export function AnalyticsHeader({ data }: { data: MeetingAnalytics }) {
  const statusBadge = data.status ? STATUS_BADGE[data.status] : null;

  return (
    <div className="space-y-4">
      <Button asChild variant="ghost" className="-ml-2 text-[var(--text-muted)]">
        <Link href="/create/recent">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to recent
        </Link>
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="min-w-0 space-y-1">
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">
            Meeting analytics
          </p>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-[var(--text-primary)] sm:text-3xl">
            {data.leadName || 'Meeting'}
          </h1>
          {data.salespersonName && (
            <p className="text-[0.95rem] text-[var(--text-muted)]">with {data.salespersonName}</p>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {statusBadge && <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>}
          {data.meetingType && <Badge variant="outline">{TYPE_LABEL[data.meetingType]}</Badge>}
        </div>
      </div>
    </div>
  );
}
